import React, { useState } from 'react';
import {
    View, StyleSheet, Platform, Image, TouchableOpacity, Linking, Alert,
    Modal, TextInput, LayoutAnimation, ActivityIndicator,
} from 'react-native';
// KAV из keyboard-controller: RN-овский внутри Modal на Android не работает —
// клавиатура накрывала форму «связаться с организатором» (CORE-944).
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useTheme } from '../context/ThemeContext';
import { useSavedEvents } from '../context/SavedEventsContext';
import { Typography, Button } from './ui';
import { UnifiedEvent, SendEventMessageRequest } from '../types/auth';
import { EVENT_TYPE_CONFIG } from '../data/events';
import { api, getFileUrl } from '../services/api';
import { useI18n } from '../i18n';
import { useAuthCTA } from '../context/AuthCTAContext';
import { useReport } from '../context/ReportContext';
import { useRepost } from '../context/RepostContext';
import { repostTargetForEvent } from '../services/repost';
import { ShareModal } from './ShareModal';
import { buildShareUrl } from '../utils/share';
import { shareOrOpenModal } from '../utils/roomShare';
import { favoriteIconName, favoriteA11yLabel } from './favoriteIcon';
import { feedRefFor } from '../utils/feedRefId';
import { typographyTokens } from '../styles/typographyTokens';

import { ACCENT_TEXT, accentInk, ON_ACCENT_TEXT } from '../styles/accentText';
import { AgapellaBadge, badgeGap } from './subscription/AgapellaBadge';
import { surfaceFill } from '../styles/platformBackgrounds';
// ─── Date formatting helpers ────────────────────────────
const formatEventDate = (startDateUtc: string, endDateUtc: string | null): string => {
    const start = new Date(startDateUtc);
    if (!endDateUtc) {
        return start.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    }
    const end = new Date(endDateUtc);
    const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
    if (sameMonth) {
        return `${start.toLocaleDateString('en-US', { month: 'long' })} ${start.getDate()}–${end.getDate()}, ${start.getFullYear()}`;
    }
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
};

const formatEventTime = (dateUtc: string): string => {
    const d = new Date(dateUtc);
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
};

// ─── EventCard Component ─────────────────────────────────
export const EventCard: React.FC<{
    event: UnifiedEvent;
    isLargeScreen: boolean;
    isAuthenticated: boolean;
    localCoverSource?: any;
    onMasterPress?: (masterProfileId: number) => void;
    onRegister: (event: UnifiedEvent) => void;
    onSave: (event: UnifiedEvent) => void;
    onSendMessage: (event: UnifiedEvent, data: SendEventMessageRequest) => Promise<void>;
    onPress?: (event: UnifiedEvent) => void;
}> = ({ event, isLargeScreen, isAuthenticated, localCoverSource, onMasterPress, onRegister, onSave, onSendMessage, onPress }) => {
    const { isDark, colors: tc } = useTheme();
    const { t } = useI18n('common');
    const { t: te } = useI18n('events');
    const savedEvents = useSavedEvents();
    // Combine server-known saved state (UnifiedEvent.isSaved from BE on
    // initial fetch) with the local SavedEventsContext so the bookmark
    // badge updates immediately on click without waiting for a refetch.
    const isBookmarked = savedEvents.isSaved(feedRefFor(event.eventType === 'practice' ? 'practice_uploaded' : 'event_saved', event.id)) || event.isSaved;
    const [showMessageModal, setShowMessageModal] = useState(false);
    const [msgText, setMsgText] = useState('');
    const [msgName, setMsgName] = useState('');
    const [msgEmail, setMsgEmail] = useState('');
    const [msgSent, setMsgSent] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [isCalendarAdded, setIsCalendarAdded] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const { showAuthCTA } = useAuthCTA();
    const { openReport } = useReport();
    const { openRepost } = useRepost();
    const typeConfig = EVENT_TYPE_CONFIG[event.eventType] || EVENT_TYPE_CONFIG.class;

    const coverUrl = event.coverImageUrl ? getFileUrl(event.coverImageUrl) : null;
    const safeTags = Array.isArray(event.tags) ? event.tags : [];
    const safeStartDate = event.startDateUtc || new Date().toISOString();
    const safeEndDate = event.endDateUtc || null;
    const dateDisplay = formatEventDate(safeStartDate, safeEndDate);
    const startTime = formatEventTime(safeStartDate);
    const endTime = safeEndDate ? formatEventTime(safeEndDate) : null;

    const handleSendMessage = async () => {
        if (!msgText.trim() || !msgName.trim()) {
            Alert.alert(t('eventContact.validationError'));
            return;
        }
        setIsSending(true);
        try {
            await onSendMessage(event, {
                senderName: msgName.trim(),
                senderEmail: msgEmail.trim() || undefined,
                content: msgText.trim(),
            });
            setMsgSent(true);
            setTimeout(() => {
                setShowMessageModal(false);
                setMsgSent(false);
                setMsgText('');
                setMsgName('');
                setMsgEmail('');
            }, 2000);
        } catch {
            Alert.alert(t('eventContact.sendFailed'));
        } finally {
            setIsSending(false);
        }
    };

    const handleAddToCalendar = () => {
        if (!isAuthenticated) {
            showAuthCTA('gated_action', 'Please register to save this event to your calendar.');
            return;
        }
        setIsCalendarAdded(prev => !prev);
        if (!isCalendarAdded) {
            const icsUrl = api.getEventIcsUrl(event.source, event.id);
            Linking.openURL(icsUrl).catch(() => {});
        }
    };

    const shareUrl = buildShareUrl('event', event.id);
    const shareMessage = `${event.title} — ${shareUrl}`;
    const shareAnalytics = { entityType: 'event', entityId: event.id, surface: 'event_card' } as const;

    // Formatting for the floating date badge
    const badgeDateObj = new Date(safeStartDate);
    const badgeDay = badgeDateObj.getDate().toString();
    const badgeMonth = badgeDateObj.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();

    return (
        <TouchableOpacity
            activeOpacity={0.92}
            onPress={() => onPress?.(event)}
            style={[
                ev.card,
                // CORE-1733 (возврат QA) — карточка события — поверхность v4.
                surfaceFill(isDark ? 'dark' : 'light'),
                Platform.OS === 'web' && {
                    boxShadow: isDark ? '0 4px 24px rgba(0,0,0,0.4)' : '0 4px 24px rgba(0,0,0,0.06)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                } as any,
            ]}
        >
            {/* ═══ COVER IMAGE ═══ */}
            <View style={ev.coverWrap}>
                {coverUrl ? (
                    <Image source={{ uri: coverUrl }} style={ev.coverImg} />
                ) : localCoverSource ? (
                    <Image source={localCoverSource} style={ev.coverImg} />
                ) : (
                    <View style={[ev.coverImg, { backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : '#F0EDEA', alignItems: 'center', justifyContent: 'center' }]}>
                        <Ionicons name="calendar-outline" size={48} color={isDark ? 'rgba(255,255,255,0.2)' : '#ccc'} />
                    </View>
                )}
                <View style={ev.coverOverlay} />

                {/* Type badge */}
                <View style={[ev.typeBadge, { backgroundColor: isDark ? 'rgba(0,0,0,0.65)' : 'rgba(255,255,255,0.85)', backdropFilter: 'blur(8px)' } as any]}>
                    <Ionicons name={typeConfig.icon as any} size={13} color={accentInk(typeConfig.color)} />
                    <Typography variant="overline" style={[ev.typeBadgeText, { color: isDark ? '#fff' : '#000', marginLeft: 4 }]}>{te(typeConfig.label)}</Typography>
                </View>

                {/* Date Badge Floating */}
                <View style={[ev.dateBadge, { backgroundColor: isDark ? 'rgba(0,0,0,0.65)' : 'rgba(255,255,255,0.85)' }]}>
                    <Typography variant="h4" style={{ color: ACCENT_TEXT } as any}>{badgeDay}</Typography>
                    <Typography variant="captionAccent" style={{ color: ACCENT_TEXT, marginTop: -2 } as any}>{badgeMonth}</Typography>
                </View>
            </View>

            {/* ═══ CARD BODY ═══ */}
            <View style={[ev.body, { padding: 20 }]}>
                {/* Title & Actions */}
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <View style={{ flex: 1, paddingRight: 16 }}>
                        <AgapellaBadge access={event.access} style={badgeGap} testID={`agapella-badge-event-${event.id}`} />
                        <Typography variant="h3" style={[{ minHeight: 56 }, { color: tc.textPrimary }] as any} numberOfLines={2}>
                            {event.title}
                        </Typography>
                        
                        {/* Compact metadata row (Time + Location) */}
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 8 }}>
                            <Typography variant="label" style={{ color: tc.textSecondary } as any}>
                                {startTime} {endTime ? `— ${endTime}` : ''}
                            </Typography>
                            <Typography variant="label" style={{ color: tc.textMuted }}>•</Typography>
                            <Typography variant="label" style={{ color: tc.textSecondary } as any} numberOfLines={1}>
                                {event.isOnline ? t('events.online') : t('events.locationTba')}
                            </Typography>
                        </View>
                    </View>
                    
                    {/* Share/Save icons top right */}
                    <View style={{ flexDirection: 'row', marginTop: -4 }}>
                        <TouchableOpacity style={{ padding: 6 }} activeOpacity={0.7} onPress={(e) => { e.stopPropagation(); handleAddToCalendar(); }}>
                            <Ionicons name={isCalendarAdded ? "calendar" : "calendar-outline"} size={22} color={isCalendarAdded ? '#16A34A' : tc.textMuted} />
                        </TouchableOpacity>
                        <TouchableOpacity
                            style={{ padding: 6 }}
                            activeOpacity={0.7}
                            accessibilityRole="button"
                            accessibilityLabel={favoriteA11yLabel(isBookmarked)}
                            onPress={(e) => { e.stopPropagation(); onSave(event); }}
                        >
                            <Ionicons name={favoriteIconName(isBookmarked)} size={22} color={isBookmarked ? ACCENT_TEXT : tc.textMuted} />
                        </TouchableOpacity>
                        {/* CORE-786 — a phone gets the OS sheet, a desktop the popup. */}
                        <TouchableOpacity style={{ padding: 6 }} activeOpacity={0.7} onPress={(e) => { e.stopPropagation(); void shareOrOpenModal({ url: shareUrl, title: event.title, message: shareMessage }, shareAnalytics, () => setShowShareModal(true)); }}>
                            <Ionicons name="share-social-outline" size={22} color={tc.textMuted} />
                        </TouchableOpacity>
                        {/* CORE-1693 — репост события мастера в свою ленту. Платформенные
                            события (`agape`) сервер репостить не умеет. */}
                        {event.source === 'master' && (
                            <TouchableOpacity
                                testID={`event-repost-${event.id}`}
                                style={{ padding: 6 }}
                                activeOpacity={0.7}
                                accessibilityRole="button"
                                accessibilityLabel={t('repost.actionA11y')}
                                onPress={(e) => {
                                    e.stopPropagation();
                                    openRepost(repostTargetForEvent(event.id, 'master'), {
                                        kind: 'event',
                                        title: event.title,
                                        text: event.description,
                                        authorName: event.organizerName,
                                        coverUrl: event.coverImageUrl,
                                        startsAtUtc: event.startDateUtc,
                                    });
                                }}
                            >
                                <Ionicons name="repeat-outline" size={22} color={tc.textMuted} />
                            </TouchableOpacity>
                        )}
                        {/* CORE-26 — жалоба на событие. */}
                        <TouchableOpacity style={{ padding: 6 }} activeOpacity={0.7} onPress={(e) => { e.stopPropagation(); openReport({ targetType: 'event', targetId: String(event.id) }); }}>
                            <Ionicons name="flag-outline" size={22} color={tc.textMuted} />
                        </TouchableOpacity>
                    </View>
                </View>

                {/* Footer: Tags and Price */}
                <View style={{ flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'space-between', marginTop: 20 }}>
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, flex: 1 }}>
                        <View style={[ev.tag, { backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)', borderColor: 'transparent', paddingHorizontal: 10, paddingVertical: 6, borderRadius: 6 }]}>
                            <Typography variant="captionAccent" style={{ color: tc.textMuted } as any}>{event.isOnline ? 'ONLINE' : 'OFFLINE'}</Typography>
                        </View>
                        {safeTags.map((tag, i) => {
                            if (i > 1) return null; // Show max 2 custom tags
                            return (
                                <View key={tag} style={[ev.tag, { backgroundColor: 'transparent', borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)', paddingHorizontal: 10, paddingVertical: 6, borderRadius: 6 }]}>
                                    <Typography variant="captionAccent" style={{ color: tc.textMuted } as any}>{tag.toUpperCase()}</Typography>
                                </View>
                            );
                        })}
                    </View>
                </View>
            </View>

            {/* ═══ MESSAGE MODAL ═══ */}
            <Modal
                visible={showMessageModal}
                transparent
                animationType="fade"
                onRequestClose={() => setShowMessageModal(false)}
            >
                <TouchableOpacity
                    activeOpacity={1}
                    onPress={() => setShowMessageModal(false)}
                    style={ev.modalOverlay}
                >
                    <KeyboardAvoidingView behavior="padding" style={ev.modalCenter}>
                        <TouchableOpacity activeOpacity={1} onPress={() => { }} style={[
                            ev.modalCard,
                            { backgroundColor: isDark ? tc.surfaceElevated : '#fff' },
                            Platform.OS === 'web' && { boxShadow: isDark ? '0 24px 80px rgba(0,0,0,0.6)' : '0 24px 80px rgba(0,0,0,0.15)' } as any,
                        ]}>
                            {/* Modal Header */}
                            <View style={ev.modalHeader}>
                                <View style={{ flex: 1 }}>
                                    <Typography variant="h4" style={[ev.modalTitle, { color: tc.textPrimary }]}>{t('eventContact.title')}</Typography>
                                    <Typography variant="caption" style={{ color: tc.textMuted, marginTop: 4 }}>{t('eventContact.aboutPrefix', { title: event.title })}</Typography>
                                </View>
                                <TouchableOpacity
                                    onPress={() => setShowMessageModal(false)}
                                    style={[ev.modalClose, { backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' }]}
                                >
                                    <Ionicons name="close" size={20} color={tc.textMuted} />
                                </TouchableOpacity>
                            </View>

                            {msgSent ? (
                                <View style={ev.sentContainer}>
                                    <View style={ev.sentIconWrap}>
                                        <Ionicons name="checkmark-circle" size={56} color="#2D8A4E" />
                                    </View>
                                    <Typography variant="h4" style={{ color: tc.textPrimary, marginTop: 16 }}>{t('eventContact.messageSent')}</Typography>
                                    <Typography variant="body" style={{ color: tc.textMuted, marginTop: 8, textAlign: 'center' }}>
                                        {t('eventContact.messageDelivered')}
                                    </Typography>
                                </View>
                            ) : (
                                <>
                                    <View style={ev.formGroup}>
                                        <Typography variant="overline" style={[ev.formLabel, { color: ACCENT_TEXT }]}>{t('eventContact.nameLabel')}</Typography>
                                        <TextInput
                                            style={[ev.formInput, { color: tc.textPrimary, borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)', backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)' }]}
                                            placeholder={t('eventContact.namePlaceholder')}
                                            placeholderTextColor={tc.textMuted}
                                            value={msgName}
                                            onChangeText={setMsgName}
                                        />
                                    </View>
                                    <View style={ev.formGroup}>
                                        <Typography variant="overline" style={[ev.formLabel, { color: ACCENT_TEXT }]}>{t('eventContact.emailLabel')}</Typography>
                                        <TextInput
                                            style={[ev.formInput, { color: tc.textPrimary, borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)', backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)' }]}
                                            placeholder={t('eventContact.emailPlaceholder')}
                                            placeholderTextColor={tc.textMuted}
                                            value={msgEmail}
                                            onChangeText={setMsgEmail}
                                            keyboardType="email-address"
                                        />
                                    </View>
                                    <View style={ev.formGroup}>
                                        <Typography variant="overline" style={[ev.formLabel, { color: ACCENT_TEXT }]}>{t('eventContact.messageLabel')}</Typography>
                                        <TextInput
                                            style={[ev.formTextarea, { color: tc.textPrimary, borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)', backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)' }]}
                                            placeholder={t('eventContact.messagePlaceholder')}
                                            placeholderTextColor={tc.textMuted}
                                            value={msgText}
                                            onChangeText={setMsgText}
                                            multiline
                                            numberOfLines={5}
                                            textAlignVertical="top"
                                        />
                                    </View>
                                    <TouchableOpacity
                                        style={[ev.sendBtn, isSending && { opacity: 0.6 }]}
                                        activeOpacity={0.85}
                                        onPress={handleSendMessage}
                                        disabled={isSending}
                                    >
                                        {isSending ? (
                                            <ActivityIndicator size="small" color="#fff" style={{ marginRight: 10 }} />
                                        ) : (
                                            <Ionicons name="send" size={16} color="#fff" style={{ marginRight: 10 }} />
                                        )}
                                        <Typography variant="overline" style={{ color: '#fff' }}>
                                            {isSending ? t('buttons.sending') : t('buttons.sendMessage')}
                                        </Typography>
                                    </TouchableOpacity>
                                    {event.contactEmail && (
                                        <TouchableOpacity
                                            style={[ev.emailFallback, { borderTopColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)' }]}
                                            onPress={() => Linking.openURL(`mailto:${event.contactEmail}?subject=${encodeURIComponent('Re: ' + event.title)}`)}
                                        >
                                            <Ionicons name="mail-outline" size={16} color={tc.textMuted} style={{ marginRight: 8 }} />
                                            <Typography variant="caption" style={{ color: tc.textMuted }}>{t('eventContact.emailFallback', { email: event.contactEmail })}</Typography>
                                        </TouchableOpacity>
                                    )}
                                </>
                            )}
                        </TouchableOpacity>
                    </KeyboardAvoidingView>
                </TouchableOpacity>
            </Modal>

            {/* ═══ SHARE MODAL ═══ */}
            <ShareModal
                visible={showShareModal}
                onClose={() => setShowShareModal(false)}
                url={shareUrl}
                title={event.title}
                message={shareMessage}
                analytics={shareAnalytics}
            />
        </TouchableOpacity>
    );
};

// ─── Styles — pixel-match with MasterDetailScreen evStyles ──
const ev = StyleSheet.create({
    card: {
        borderRadius: 20, overflow: 'hidden', backgroundColor: '#fff',
    } as any,

    // ─── Cover ───
    coverWrap: {
        position: 'relative', aspectRatio: 1.35, overflow: 'hidden',
    } as any,
    coverImg: {
        width: '100%', height: '100%', resizeMode: 'cover',
    } as any,
    coverOverlay: {
        ...StyleSheet.absoluteFillObject,
        ...(Platform.OS === 'web' && { backgroundImage: 'linear-gradient(to bottom, rgba(0,0,0,0) 40%, rgba(0,0,0,0.4) 100%)' }),
        ...(Platform.OS !== 'web' && { backgroundColor: 'rgba(0,0,0,0.15)' }),
    } as any,

    // ─── Badges ───
    typeBadge: {
        position: 'absolute', top: 14, left: 14, flexDirection: 'row', alignItems: 'center',
        gap: 5, paddingHorizontal: 10, paddingVertical: 5, borderRadius: 8,
    } as any,
    typeBadgeText: {
        ...typographyTokens.label,
        color: '#fff',
    } as any,
    dateBadge: {
        width: 50, height: 56, borderRadius: 12, alignItems: 'center', justifyContent: 'center',
        position: 'absolute', top: 14, right: 14,
        ...(Platform.OS === 'web' && { backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }),
    } as any,
    priceBadge: {
        position: 'absolute', bottom: 14, right: 14,
        paddingHorizontal: 14, paddingVertical: 7, borderRadius: 10,
        ...(Platform.OS === 'web' && { backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }),
    } as any,
    priceText: {
        ...typographyTokens.bodyAccent,
    } as any,
    onlineBadge: {
        position: 'absolute', bottom: 14, left: 14, flexDirection: 'row', alignItems: 'center',
        gap: 6, backgroundColor: 'rgba(0,0,0,0.55)', paddingHorizontal: 10, paddingVertical: 5, borderRadius: 8,
        ...(Platform.OS === 'web' && { backdropFilter: 'blur(8px)' }),
    } as any,
    onlineDot: {
        width: 7, height: 7, borderRadius: 4, backgroundColor: '#4CAF50',
    },
    onlineText: {
        ...typographyTokens.label,
        color: '#fff',
    } as any,
    offlineBadge: {
        position: 'absolute', bottom: 14, left: 14, flexDirection: 'row', alignItems: 'center',
        gap: 5, backgroundColor: '#E98245', paddingHorizontal: 10, paddingVertical: 5, borderRadius: 8,
    } as any,

    // ─── Body ───
    body: {
        padding: 28,
    },
    titleRow: {
        flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16,
    } as any,
    title: {
        ...typographyTokens.uiH2,
    } as any,
    subtitle: {
        ...typographyTokens.body,
        marginTop: 6, opacity: 0.7,
    } as any,
    expandIcon: {
        width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center',
        marginTop: 2,
    } as any,

    // ─── Header Utility Actions ───
    headerActions: {
        flexDirection: 'row', alignItems: 'center', gap: 4,
    } as any,
    headerIconBtn: {
        width: 34, height: 34, alignItems: 'center', justifyContent: 'center',
        borderRadius: 10, opacity: 0.6,
        ...(Platform.OS === 'web' && { cursor: 'pointer', transition: 'opacity 0.15s ease' }),
    } as any,

    // ─── Quick info ───
    quickInfo: {
        flexDirection: 'row', flexWrap: 'wrap', gap: 16, marginTop: 18, paddingTop: 18,
    } as any,
    infoChip: {
        flexDirection: 'row', alignItems: 'center', gap: 6,
    } as any,
    infoText: {
        ...typographyTokens.label,
    } as any,
    calendarLink: {
        flexDirection: 'row', alignItems: 'center', gap: 4,
        paddingHorizontal: 8, paddingVertical: 3, borderRadius: 6, marginLeft: -4,
        ...(Platform.OS === 'web' && { cursor: 'pointer', transition: 'opacity 0.15s ease' }),
    } as any,

    // ─── Tags ───
    tagsRow: {
        flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 16,
    } as any,
    tag: {
        paddingHorizontal: 12, paddingVertical: 5, borderRadius: 20, borderWidth: 1,
    },
    tagText: {
        ...typographyTokens.label,
    } as any,

    // ─── Expanded ───
    expandedContent: {
        marginTop: 4,
    },
    divider: {
        height: 1, marginVertical: 20,
    },
    descSection: {
        marginBottom: 20,
    },
    sectionLabel: {
        ...typographyTokens.label,
        marginBottom: 10,
    } as any,
    descText: {
        ...typographyTokens.body,
    } as any,

    // ─── Detail cards ───
    detailCard: {
        borderRadius: 14, padding: 18, marginBottom: 14,
    },
    detailRow: {
        flexDirection: 'row', alignItems: 'center', gap: 14,
    } as any,
    detailIconWrap: {
        width: 42, height: 42, borderRadius: 12, alignItems: 'center', justifyContent: 'center',
    } as any,
    detailTitle: {
        ...typographyTokens.bodyAccent,
    } as any,
    detailSub: {
        ...typographyTokens.caption,
        marginTop: 3,
    },

    // ─── Contact ───
    contactGrid: {
        gap: 12,
    } as any,
    contactItem: {
        flexDirection: 'row', alignItems: 'center', gap: 10,
    } as any,
    contactText: {
        ...typographyTokens.body,
    },

    // ─── Spots ───
    spotsSection: {
        marginTop: 6, marginBottom: 8,
    },
    spotsHeader: {
        flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10,
    } as any,
    spotsText: {
        ...typographyTokens.label,
    } as any,
    progressBar: {
        height: 6, borderRadius: 3, overflow: 'hidden',
    },
    progressFill: {
        height: '100%', borderRadius: 3, backgroundColor: '#2D8A4E',
    } as any,

    // ─── CTAs ───
    ctaRow: {
        flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 22,
    } as any,
    ctaPrimary: {
        flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
        height: 48, borderRadius: 999, backgroundColor: '#F45122',
        borderWidth: 1, borderColor: '#F26A36',
        ...(Platform.OS === 'web' && {
          cursor: 'pointer', transition: 'all 0.2s ease',
          backgroundImage: 'linear-gradient(100deg, #F45122 0%, #FF955D 100%)',
          boxShadow: '0 8px 24px rgba(244,81,34,0.22)',
        }),
    } as any,
    ctaPrimaryText: {
        ...typographyTokens.label,
        color: ON_ACCENT_TEXT,
    } as any,
    ctaIconBtn: {
        width: 48, height: 48, borderRadius: 24, borderWidth: 1.5,
        alignItems: 'center', justifyContent: 'center',
        ...(Platform.OS === 'web' && { cursor: 'pointer', transition: 'all 0.2s ease' }),
    } as any,
    ctaSecondary: {
        flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
        height: 48, borderRadius: 999, borderWidth: 1.5,
        ...(Platform.OS === 'web' && { cursor: 'pointer', transition: 'all 0.2s ease' }),
    } as any,
    ctaSecondaryText: {
        ...typographyTokens.label,
    } as any,
    ctaOutlined: {
        flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
        height: 48, borderRadius: 999, borderWidth: 1.5,
        ...(Platform.OS === 'web' && { cursor: 'pointer', transition: 'all 0.2s ease' }),
    } as any,
    ctaOutlinedText: {
        ...typographyTokens.label,
    } as any,

    // ─── Message Modal ───
    modalOverlay: {
        flex: 1, justifyContent: 'center', alignItems: 'center',
        backgroundColor: 'rgba(0,0,0,0.6)',
        ...(Platform.OS === 'web' && { backdropFilter: 'blur(8px)' }),
    } as any,
    modalCenter: {
        width: '100%', alignItems: 'center', justifyContent: 'center',
    } as any,
    modalCard: {
        width: '90%', maxWidth: 480, borderRadius: 24, padding: 28,
    } as any,
    modalHeader: {
        flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24,
    } as any,
    modalTitle: {
        ...typographyTokens.uiH3,
    } as any,
    modalClose: {
        width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center',
    } as any,
    sentContainer: {
        alignItems: 'center', paddingVertical: 32,
    } as any,
    sentIconWrap: {
        width: 80, height: 80, borderRadius: 40, alignItems: 'center', justifyContent: 'center',
        backgroundColor: 'rgba(45,138,78,0.1)',
    } as any,
    formGroup: {
        marginBottom: 18,
    },
    formLabel: {
        ...typographyTokens.label,
        marginBottom: 8,
    } as any,
    formInput: {
        ...typographyTokens.input,
        height: 48, borderRadius: 12, borderWidth: 1, paddingHorizontal: 16,
        ...(Platform.OS === 'web' && { outlineStyle: 'none' }),
    } as any,
    formTextarea: {
        ...typographyTokens.input,
        height: 120, borderRadius: 12, borderWidth: 1, paddingHorizontal: 16, paddingTop: 14,
        ...(Platform.OS === 'web' && { outlineStyle: 'none', resize: 'vertical' }),
    } as any,
    sendBtn: {
        flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
        height: 50, borderRadius: 14, backgroundColor: '#6B5CE7', marginTop: 6,
        ...(Platform.OS === 'web' && { cursor: 'pointer', transition: 'all 0.2s ease' }),
    } as any,
    emailFallback: {
        flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
        paddingVertical: 14, marginTop: 14, borderTopWidth: 1,
        ...(Platform.OS === 'web' && { cursor: 'pointer' }),
    } as any,
});

export default EventCard;
